import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/utils/cn";
import type { ProfileSearchResponse } from "@/types/settings";

interface EmailLookupAutocompleteProps {
  id: string;
  value: string;
  suggestions: ProfileSearchResponse[];
  isLoading: boolean;
  disabled?: boolean;
  error?: string;
  onInputChange: (value: string) => void;
  onSelect: (profile: ProfileSearchResponse) => void;
}

export function EmailLookupAutocomplete({
  id,
  value,
  suggestions,
  isLoading,
  disabled = false,
  error,
  onInputChange,
  onSelect
}: EmailLookupAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const listId = `${id}-listbox`;
  const hasSuggestions = suggestions.length > 0;

  const isExpanded = open && hasSuggestions;

  const activeDescendant = useMemo(() => {
    if (!isExpanded || activeIndex < 0 || activeIndex >= suggestions.length) return undefined;
    return `${id}-option-${activeIndex}`;
  }, [activeIndex, id, isExpanded, suggestions.length]);

  function selectProfile(profile: ProfileSearchResponse) {
    onSelect(profile);
    setOpen(false);
    setActiveIndex(-1);
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          onInputChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Delay close so option click can be processed.
          setTimeout(() => setOpen(false), 120);
        }}
        onKeyDown={(e) => {
          if (!isExpanded) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((prev) => (prev + 1) % suggestions.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
          } else if (e.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
              e.preventDefault();
              selectProfile(suggestions[activeIndex]);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
            setActiveIndex(-1);
          }
        }}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isExpanded}
        aria-controls={listId}
        aria-activedescendant={activeDescendant}
        className={cn(
          "w-full border border-slate-300 px-3 py-2 text-sm",
          error && "border-red-500",
          disabled && "bg-slate-50 text-slate-500"
        )}
        placeholder="Search by name or email"
      />

      {isLoading && !disabled ? (
        <Loader2 className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 animate-spin text-slate-500" />
      ) : null}

      {isExpanded ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto border border-slate-300 bg-white shadow-lg"
        >
          {suggestions.map((profile, idx) => (
            <li
              key={`${profile.email}-${idx}`}
              id={`${id}-option-${idx}`}
              role="option"
              aria-selected={activeIndex === idx}
              className={cn(
                "cursor-pointer px-3 py-2 text-sm hover:bg-slate-100",
                activeIndex === idx && "bg-slate-100"
              )}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectProfile(profile);
              }}
            >
              <div className="font-medium text-slate-900">{profile.fullName}</div>
              <div className="text-xs text-slate-600">{profile.email}</div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
